import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { GameEvent } from '@src/core/EventQueue';
import { kill, spawnFx } from '@src/ecs/archetypes';
import {
  Dying,
  Enemy,
  EnemyKind,
  Health,
  Mine,
  MINE_FUSED,
  MINE_IDLE,
  Player,
  Position,
  Projectile,
  Radius,
  Sprite,
  SpriteId,
} from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, requestDecal, RoomPhase, type GameContext } from '@src/ecs/world';
import { dealDamage } from '@src/game/Combat';

const MINE_SCORCH = 0x2a1008;
const MINE_FIRE = 0xff5a18;

/**
 * Floor mines: idle glow, fuse on contact (wizard, fiends, or a stray shot),
 * then a wide blast that goes through the same damage path as everything else.
 */
export class MineSystem implements GameSystem {
  private ctx!: GameContext;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const mines = query(ctx.world, [Mine, Position, Radius, Sprite]);
    if (mines.length === 0) return;

    const players = query(ctx.world, [Player, Position, Radius, Health]);
    const enemies = query(ctx.world, [Enemy, Position, Radius, Health]);
    const shots = query(ctx.world, [Projectile, Position, Radius]);

    for (let i = 0; i < mines.length; i++) {
      const eid = mines[i];
      if (Dying.flag[eid]) continue;
      const x = Position.x[eid];
      const y = Position.y[eid];
      const r = Radius.r[eid];

      if (Mine.state[eid] === MINE_IDLE) {
        Sprite.flash[eid] = 0.12 + 0.14 * (0.5 + 0.5 * Math.sin(ctx.time * 7 + eid));
        Sprite.squash[eid] = 0.12 * Math.sin(ctx.time * 5 + eid * 0.7);
        if (ctx.rng.chance(dt * 2.2)) emitParticles(ctx.fx, ParticleKind.Embers, x, y, 0, 1, 0.16, MINE_FIRE);
        if (ctx.room.phase !== RoomPhase.Transition && this.shouldArm(eid, x, y, r, players, enemies, shots)) {
          Mine.state[eid] = MINE_FUSED;
          Mine.fuse[eid] = Config.mines.fuseSeconds;
          Sprite.flash[eid] = 1;
          Sprite.pop[eid] = 0.45;
          ctx.events.push(GameEvent.MineArmed, eid, 0, x, y);
        }
        continue;
      }

      Mine.fuse[eid] -= dt;
      const panic = 1 - Math.max(0, Mine.fuse[eid]) / Config.mines.fuseSeconds;
      Sprite.flash[eid] = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(ctx.time * (18 + panic * 28)));
      Sprite.squash[eid] = 0.2 + panic * 0.35;
      Sprite.scale[eid] = 1.15 + panic * 0.35;
      if (Mine.fuse[eid] > 0) continue;
      this.detonate(eid, x, y);
    }
  }

  private shouldArm(
    eid: number,
    x: number,
    y: number,
    r: number,
    players: ArrayLike<number>,
    enemies: ArrayLike<number>,
    shots: ArrayLike<number>,
  ): boolean {
    for (let i = 0; i < players.length; i++) {
      const pid = players[i];
      if (Player.dead[pid] || Dying.flag[pid]) continue;
      if (overlaps(x, y, r, Position.x[pid], Position.y[pid], Radius.r[pid])) return true;
    }
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Dying.flag[e] || Health.hp[e] <= 0) continue;
      if (Enemy.kind[e] === EnemyKind.Mimic && Enemy.mode[e] < 2) continue;
      if (Enemy.kind[e] === EnemyKind.Wraith && Health.invuln[e] > 0) continue;
      if (overlaps(x, y, r, Position.x[e], Position.y[e], Radius.r[e])) return true;
    }
    for (let i = 0; i < shots.length; i++) {
      const s = shots[i];
      if (Dying.flag[s] || s === eid) continue;
      if (overlaps(x, y, r, Position.x[s], Position.y[s], Radius.r[s])) return true;
    }
    return false;
  }

  private detonate(eid: number, x: number, y: number): void {
    const ctx = this.ctx;
    const c = Config.mines;
    const blast = c.blastRadius;
    ctx.events.push(GameEvent.MineBlast, eid, 0, x, y);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, Config.fx.hitStopMs * 0.9);
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 1.1;
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * 1.1;
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.85);
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.28);
    requestDecal(ctx.fx, x, y, MINE_SCORCH, blast * 0.95);
    spawnFx(ctx, SpriteId.BombBlast, x, y, ctx.rng.next() * Math.PI * 2, blast * 1.55, 0xffffff, 0.72);
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, blast * 1.05, MINE_FIRE, 0.52);
    emitParticles(ctx.fx, ParticleKind.Embers, x, y, 0, 0, 1.8, MINE_FIRE);
    emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, 0, 0, 1.1, 0xffd080);

    const players = query(ctx.world, [Player, Position, Radius, Health]);
    for (let i = 0; i < players.length; i++) {
      const pid = players[i];
      if (Player.dead[pid]) continue;
      const dx = Position.x[pid] - x;
      const dy = Position.y[pid] - y;
      const reach = blast + Radius.r[pid];
      const d2 = dx * dx + dy * dy;
      if (d2 > reach * reach) continue;
      const d = Math.sqrt(d2) || 1;
      dealDamage(ctx, pid, c.playerDamage, (dx / d) * c.knockback, (dy / d) * c.knockback, 1, 0);
    }

    const enemies = query(ctx.world, [Enemy, Position, Radius, Health]);
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Dying.flag[e] || Health.hp[e] <= 0) continue;
      const dx = Position.x[e] - x;
      const dy = Position.y[e] - y;
      const reach = blast + Radius.r[e];
      const d2 = dx * dx + dy * dy;
      if (d2 > reach * reach) continue;
      const d = Math.sqrt(d2) || 1;
      const kb = Enemy.kind[e] === EnemyKind.Boss ? c.knockback * 0.25 : c.knockback;
      dealDamage(ctx, e, c.enemyDamage, (dx / d) * kb, (dy / d) * kb, 1, 0);
    }

    kill(eid);
  }
}

function overlaps(ax: number, ay: number, ar: number, bx: number, by: number, br: number): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  const r = ar + br;
  return dx * dx + dy * dy <= r * r;
}
